import React from "react";
import ReactDOM from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import "@/index.css";
import App from "@/App";

// Canonical-domain redirect. Runs before anything else mounts so a
// previously-installed PWA icon, an old bookmark, or a stale link (any of
// which point at whatever domain was live when the user first saved it —
// e.g. dialflow.up.railway.app or a bare ccsshop.in) transparently lands on
// the one production domain instead of a dead/legacy page. localhost stays
// untouched for local development.
const CANONICAL_HOST = "www.ccsshop.in";
if (
  window.location.hostname !== CANONICAL_HOST &&
  window.location.hostname !== "localhost" &&
  window.location.hostname !== "127.0.0.1"
) {
  window.location.replace(
    `https://${CANONICAL_HOST}${window.location.pathname}${window.location.search}${window.location.hash}`,
  );
}

// Pin the PWA manifest's start_url to the current shop BEFORE React even
// mounts. Chrome can snapshot install-eligibility data (including
// start_url) very early in page load — often before any API round-trip
// finishes — so tenantContext.jsx's async brand/manifest swap sometimes
// loses that race, which is why a customer's "Add to Home Screen" icon can
// launch into the owner console instead of their shop. Deriving the slug
// straight from the URL needs no network call, so this always wins the
// race; tenantContext.jsx still upgrades it afterwards with the shop's
// real name, colours and logo once the backend responds.
const RESERVED_SHOP_PATHS = new Set(["signup", "login", "account", "orders", "checkout", "chat", "track"]);
const shopSlugMatch = window.location.pathname.match(/^\/shop\/([^/]+)/);
const shopSlug = shopSlugMatch && !RESERVED_SHOP_PATHS.has(shopSlugMatch[1]) ? shopSlugMatch[1] : null;
if (shopSlug) {
  // Every URL here must be absolute. A manifest served from a blob: URL has
  // no meaningful base to resolve relative references against — Chrome logs
  // "URL is invalid" for start_url/scope/icon src alike and ignores the
  // manifest outright, silently undoing this whole race-condition fix.
  const origin = window.location.origin;
  const quickManifest = {
    name: "DialFlow Shop",
    short_name: "Shop",
    start_url: `${origin}/shop/${shopSlug}`,
    scope: `${origin}/shop/${shopSlug}`,
    display: "standalone",
    orientation: "portrait",
    background_color: "#ffffff",
    theme_color: "#0055FF",
    icons: [
      { src: `${origin}/icons/icon-192.png`, sizes: "192x192", type: "image/png", purpose: "any" },
      { src: `${origin}/icons/icon-512.png`, sizes: "512x512", type: "image/png", purpose: "any" },
      { src: `${origin}/icons/icon-maskable.png`, sizes: "512x512", type: "image/png", purpose: "maskable" },
    ],
  };
  const blobUrl = URL.createObjectURL(
    new Blob([JSON.stringify(quickManifest)], { type: "application/json" }),
  );
  let manifestLink = document.querySelector('link[rel="manifest"]');
  if (!manifestLink) {
    manifestLink = document.createElement("link");
    manifestLink.rel = "manifest";
    document.head.appendChild(manifestLink);
  }
  manifestLink.setAttribute("href", blobUrl);
}

// Register the DialFlow PWA service worker so Chrome/Edge show the
// "Install app" prompt on Android and Windows.
if ("serviceWorker" in navigator && window.location.protocol === "https:") {
  window.addEventListener("load", () => {
    navigator.serviceWorker.register("/sw.js").catch(() => {
      /* best-effort — ignore registration failures in preview envs */
    });
  });
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      refetchOnWindowFocus: false,
    },
  },
});

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </React.StrictMode>,
);
